// Plein écran des vidéos sur téléphone.
// Le lecteur (/lecteur/youtube/…) est une page intégrée dans une iframe. Sur iPhone, le navigateur
// n'autorise le plein écran que pour une balise vidéo native, jamais pour une page ; dans certaines
// applications, la demande est refusée. Le lecteur demande alors à la page qui l'intègre d'agrandir
// son iframe à tout l'écran : c'est ce que fait ce module. On en sort par le même bouton, par le
// geste « retour » du téléphone ou par Échap.
import { origineSite } from "./lecteurYoutube";

const CLASSE = "ql-video-plein-ecran";
const CLASSE_PAGE = "ql-video-plein-ecran-actif";
let actif = null;
let ancetres = [];

function trouverIframe(source) {
  return [...document.querySelectorAll("iframe")].find((iframe) => iframe.contentWindow === source) || null;
}

function informer(iframe, etat) {
  try {
    iframe.contentWindow?.postMessage({ source: "ql-parent", type: "plein-ecran", actif: etat }, "*");
  } catch { /* lecteur déjà fermé */ }
}

// Un parent transformé (animation, fenêtre) enfermerait l'iframe « fixe » dans sa boîte :
// ces propriétés sont neutralisées le temps du plein écran, puis rétablies.
function neutraliserAncetres(iframe) {
  ancetres = [];
  for (let el = iframe.parentElement; el && el !== document.body; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    if (style.transform !== "none" || style.filter !== "none" || (style.contain && style.contain !== "none")
        || style.perspective !== "none" || /transform|filter/.test(style.willChange || "")) {
      ancetres.push([el, el.getAttribute("style")]);
      ["transform", "filter", "contain", "perspective", "will-change"].forEach((p) => el.style.setProperty(p, "none", "important"));
    }
  }
}

function retablirAncetres() {
  ancetres.forEach(([el, style]) => { if (style === null) el.removeAttribute("style"); else el.setAttribute("style", style); });
  ancetres = [];
}

export function sortirPleinEcranVideo({ parRetour = false } = {}) {
  if (!actif) return;
  const iframe = actif;
  actif = null;
  iframe.classList.remove(CLASSE);
  document.body.classList.remove(CLASSE_PAGE);
  retablirAncetres();
  informer(iframe, false);
  const natif = document.fullscreenElement || document.webkitFullscreenElement;
  if (natif) (document.exitFullscreen || document.webkitExitFullscreen)?.call(document)?.catch?.(() => {});
  if (!parRetour && window.history.state?.qlVideoPleinEcran) window.history.back();
}

export function entrerPleinEcranVideo(iframe) {
  if (!iframe || actif === iframe) return;
  if (actif) sortirPleinEcranVideo();
  actif = iframe;
  neutraliserAncetres(iframe);
  iframe.classList.add(CLASSE);
  document.body.classList.add(CLASSE_PAGE);
  informer(iframe, true);
  window.history.pushState({ ...(window.history.state || {}), qlVideoPleinEcran: true }, "");
  // Là où c'est permis (Android), vrai plein écran et passage à l'horizontale.
  const demande = iframe.requestFullscreen || iframe.webkitRequestFullscreen;
  if (demande) {
    try {
      Promise.resolve(demande.call(iframe))
        .then(() => window.screen?.orientation?.lock?.("landscape"))
        .catch(() => {});
    } catch { /* plein écran CSS seulement */ }
  }
}

export function installerPleinEcranVideo() {
  window.addEventListener("message", (event) => {
    const donnees = event.data;
    if (donnees?.source !== "ql-lecteur" || donnees.type !== "plein-ecran") return;
    if (event.origin !== origineSite() && event.origin !== window.location.origin) return;
    const iframe = trouverIframe(event.source);
    if (!iframe) return;
    if (donnees.valeur === false || actif === iframe) sortirPleinEcranVideo();
    else entrerPleinEcranVideo(iframe);
  });
  window.addEventListener("popstate", () => { if (actif) sortirPleinEcranVideo({ parRetour: true }); });
  document.addEventListener("keydown", (event) => { if (event.key === "Escape" && actif) sortirPleinEcranVideo(); });
  const finNatif = () => {
    // Sortie du plein écran natif par le système (geste, bouton) : on quitte aussi l'agrandissement.
    if (actif && !document.fullscreenElement && !document.webkitFullscreenElement) sortirPleinEcranVideo();
  };
  document.addEventListener("fullscreenchange", finNatif);
  document.addEventListener("webkitfullscreenchange", finNatif);
}
